import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

function TechShowcase() {
  return (
    <div className="pt-8 pb-24 bg-[#f8fafc] text-[#0f172a] relative overflow-hidden border-b border-gray-200 font-sans">
      
      {/* Background Decorative Grid Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#cbd5e1_1px,transparent_1px),linear-gradient(to_bottom,#cbd5e1_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)] opacity-20 pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Luxury Section Header - Modern Left-Aligned Corporate Layout */}
        <div className="text-left max-w-4xl mb-14">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-gray-950 tracking-tight leading-tight mb-5 font-sans">
              What We Build For You: <br className="hidden sm:block" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-emerald-800 to-teal-700">
                Custom Engineering Services
              </span>
            </h2>
            <p className="text-base sm:text-xl text-gray-600 leading-relaxed font-normal max-w-3xl">
              Beyond our core product lineup, NXV DYNAMICS operates as your full-stack R&D incubation partner. We engineer tailored PCB hardware, train edge AI vision models, and build companion cloud dashboards tailored precisely to your enterprise requirements.
            </p>
          </motion.div>
        </div>

        {/* 4 Breathtaking Custom Engineering Services (2x2 Grid) - ZERO CLUTTER, DIRECT TITLES */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
          
          {/* SERVICE 1: ROS & ARDUINO / RASPBERRY PI SYSTEMS */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            whileHover={{ y: -8 }}
            className="bg-white rounded-3xl p-8 sm:p-10 border border-gray-200/80 hover:border-blue-500/40 shadow-[0_20px_50px_rgba(0,0,0,0.05)] hover:shadow-[0_25px_60px_rgba(59,130,246,0.12)] transition-all duration-500 flex flex-col justify-between group relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-48 h-48 bg-blue-500/5 rounded-full blur-[60px] pointer-events-none group-hover:bg-blue-500/10 transition-colors"></div>

            <div>
              <h3 className="text-2xl sm:text-3xl font-black text-gray-950 mb-4 tracking-tight font-sans group-hover:text-blue-600 transition-colors">
                Custom ROS & Embedded Systems
              </h3>
              <p className="text-gray-600 text-base leading-relaxed mb-8 font-normal">
                We architect end-to-end autonomous robots, agricultural rovers, and industrial automation prototypes using ROS 2, Arduino Mega/Pro, and Raspberry Pi 5. From kinematics modeling to LiDAR SLAM mapping and multi-sensor payload integration, we turn your hardware concepts into working production units.
              </p>
            </div>

            <div className="pt-6 border-t border-gray-100 flex flex-wrap gap-2.5">
              <span className="text-xs font-mono font-bold px-3 py-1.5 bg-gray-100 text-gray-800 rounded-lg border border-gray-200/60 shadow-2xs">[ ROS 2 HUMBLE ]</span>
              <span className="text-xs font-mono font-bold px-3 py-1.5 bg-gray-100 text-gray-800 rounded-lg border border-gray-200/60 shadow-2xs">[ ARDUINO PRO ]</span>
              <span className="text-xs font-mono font-bold px-3 py-1.5 bg-gray-100 text-gray-800 rounded-lg border border-gray-200/60 shadow-2xs">[ RASPBERRY PI 5 ]</span>
              <span className="text-xs font-mono font-bold px-3 py-1.5 bg-gray-100 text-gray-800 rounded-lg border border-gray-200/60 shadow-2xs">[ SLAM & LiDAR ]</span>
            </div>
          </motion.div>

          {/* SERVICE 2: NVIDIA JETSON EDGE AI & VISION */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            whileHover={{ y: -8 }}
            className="bg-white rounded-3xl p-8 sm:p-10 border border-gray-200/80 hover:border-purple-500/40 shadow-[0_20px_50px_rgba(0,0,0,0.05)] hover:shadow-[0_25px_60px_rgba(168,85,247,0.12)] transition-all duration-500 flex flex-col justify-between group relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-48 h-48 bg-purple-500/5 rounded-full blur-[60px] pointer-events-none group-hover:bg-purple-500/10 transition-colors"></div>

            <div>
              <h3 className="text-2xl sm:text-3xl font-black text-gray-950 mb-4 tracking-tight font-sans group-hover:text-purple-600 transition-colors">
                Onboard Jetson AI & Computer Vision
              </h3>
              <p className="text-gray-600 text-base leading-relaxed mb-8 font-normal">
                Require real-time decision making without internet dependencies? We engineer specialized edge computing systems powered by NVIDIA Jetson Orin and Nano. From deep-learning crop weed classification to biometric attendance and defect sorting, we train and deploy lightweight AI directly at the edge.
              </p>
            </div>

            <div className="pt-6 border-t border-gray-100 flex flex-wrap gap-2.5">
              <span className="text-xs font-mono font-bold px-3 py-1.5 bg-gray-100 text-gray-800 rounded-lg border border-gray-200/60 shadow-2xs">[ NVIDIA JETSON ]</span>
              <span className="text-xs font-mono font-bold px-3 py-1.5 bg-gray-100 text-gray-800 rounded-lg border border-gray-200/60 shadow-2xs">[ CUDA & OPENCV ]</span>
              <span className="text-xs font-mono font-bold px-3 py-1.5 bg-gray-100 text-gray-800 rounded-lg border border-gray-200/60 shadow-2xs">[ TENSORFLOW ]</span>
              <span className="text-xs font-mono font-bold px-3 py-1.5 bg-gray-100 text-gray-800 rounded-lg border border-gray-200/60 shadow-2xs">[ YOLOV8 VISION ]</span>
            </div>
          </motion.div>

          {/* SERVICE 3: CUSTOM PCB FABRICATION & DRIVERS */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            whileHover={{ y: -8 }}
            className="bg-white rounded-3xl p-8 sm:p-10 border border-gray-200/80 hover:border-emerald-500/40 shadow-[0_20px_50px_rgba(0,0,0,0.05)] hover:shadow-[0_25px_60px_rgba(16,185,129,0.12)] transition-all duration-500 flex flex-col justify-between group relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-48 h-48 bg-emerald-500/5 rounded-full blur-[60px] pointer-events-none group-hover:bg-emerald-500/10 transition-colors"></div>

            <div>
              <h3 className="text-2xl sm:text-3xl font-black text-gray-950 mb-4 tracking-tight font-sans group-hover:text-emerald-600 transition-colors">
                Custom PCB Layout & Motor Drivers
              </h3>
              <p className="text-gray-600 text-base leading-relaxed mb-8 font-normal">
                Say goodbye to bulky off-the-shelf modules. We engineer proprietary multi-layer PCBs, high-power 40A dual H-bridge motor drivers, power distribution boards (PDBs), and miniaturized sensor arrays designed to fit your exact voltage specifications and enclosure constraints.
              </p>
            </div>

            <div className="pt-6 border-t border-gray-100 flex flex-wrap gap-2.5">
              <span className="text-xs font-mono font-bold px-3 py-1.5 bg-gray-100 text-gray-800 rounded-lg border border-gray-200/60 shadow-2xs">[ 40A DUAL H-BRIDGE ]</span>
              <span className="text-xs font-mono font-bold px-3 py-1.5 bg-gray-100 text-gray-800 rounded-lg border border-gray-200/60 shadow-2xs">[ ALTIUM / KICAD ]</span>
              <span className="text-xs font-mono font-bold px-3 py-1.5 bg-gray-100 text-gray-800 rounded-lg border border-gray-200/60 shadow-2xs">[ MULTI-LAYER PCB ]</span>
              <span className="text-xs font-mono font-bold px-3 py-1.5 bg-gray-100 text-gray-800 rounded-lg border border-gray-200/60 shadow-2xs">[ SMT ASSEMBLY ]</span>
            </div>
          </motion.div>

          {/* SERVICE 4: WEB & MOBILE COMPANION APPS */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            whileHover={{ y: -8 }}
            className="bg-white rounded-3xl p-8 sm:p-10 border border-gray-200/80 hover:border-amber-500/40 shadow-[0_20px_50px_rgba(0,0,0,0.05)] hover:shadow-[0_25px_60px_rgba(245,158,11,0.12)] transition-all duration-500 flex flex-col justify-between group relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 w-48 h-48 bg-amber-500/5 rounded-full blur-[60px] pointer-events-none group-hover:bg-amber-500/10 transition-colors"></div>

            <div>
              <h3 className="text-2xl sm:text-3xl font-black text-gray-950 mb-4 tracking-tight font-sans group-hover:text-amber-600 transition-colors">
                Custom Web & Mobile IoT Dashboards
              </h3>
              <p className="text-gray-600 text-base leading-relaxed mb-8 font-normal">
                Hardware is only half the battle. We build bespoke React web portals, Flutter/Android companion mobile apps, and low-latency WebSocket cloud backends that let users monitor live telemetry, stream camera feeds, and command field rovers remotely from anywhere in the world.
              </p>
            </div>

            <div className="pt-6 border-t border-gray-100 flex flex-wrap gap-2.5">
              <span className="text-xs font-mono font-bold px-3 py-1.5 bg-gray-100 text-gray-800 rounded-lg border border-gray-200/60 shadow-2xs">[ REACT & FLUTTER ]</span>
              <span className="text-xs font-mono font-bold px-3 py-1.5 bg-gray-100 text-gray-800 rounded-lg border border-gray-200/60 shadow-2xs">[ WEBSOCKETS ]</span>
              <span className="text-xs font-mono font-bold px-3 py-1.5 bg-gray-100 text-gray-800 rounded-lg border border-gray-200/60 shadow-2xs">[ LIVE TELEMETRY ]</span>
              <span className="text-xs font-mono font-bold px-3 py-1.5 bg-gray-100 text-gray-800 rounded-lg border border-gray-200/60 shadow-2xs">[ REMOTE CONTROL ]</span>
            </div>
          </motion.div>

        </div>

        {/* Elevated High-Contrast Consultation Banner */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.98 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="bg-gradient-to-r from-gray-900 via-[#0a1120] to-black text-white rounded-3xl p-8 sm:p-14 border border-gray-800 shadow-[0_25px_60px_rgba(0,0,0,0.8)] flex flex-col lg:flex-row items-center justify-between gap-8 relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-500/15 rounded-full blur-[120px] pointer-events-none"></div>
          <div className="absolute bottom-0 left-1/3 w-80 h-80 bg-blue-500/10 rounded-full blur-[100px] pointer-events-none"></div>

          <div className="max-w-2xl text-center lg:text-left relative z-10">
            <span className="text-xs font-mono font-bold text-emerald-400 uppercase tracking-[0.25em] block mb-3">
              START YOUR OEM PROJECT TODAY
            </span>
            <h4 className="text-3xl sm:text-4xl font-black text-white mb-4 tracking-tight">
              Ready to Turn Your Robotics Concept Into Reality?
            </h4>
            <p className="text-gray-300 text-base sm:text-lg leading-relaxed font-light">
              Whether you need a proprietary 40A motor driver PCB, a specialized ROS 2 agricultural rover prototype, or a complete companion cloud dashboard, connect directly with our engineering architects in Bikaner.
            </p>
          </div>

          <div className="relative z-10 shrink-0">
            <Link 
              to="/contact" 
              className="px-9 py-5 bg-gradient-to-r from-emerald-400 via-teal-400 to-emerald-500 hover:from-emerald-300 hover:to-teal-300 text-gray-950 font-black text-sm uppercase tracking-wider rounded-2xl shadow-[0_0_30px_rgba(16,185,129,0.35)] hover:shadow-[0_0_50px_rgba(16,185,129,0.6)] transition-all inline-flex items-center gap-3 hover:scale-105 active:scale-95"
            >
              <span>Connect With Bikaner Engineers</span>
              <span className="text-lg font-black">&rarr;</span>
            </Link>
          </div>
        </motion.div>

      </div>
    </div>
  );
}

export default TechShowcase;
